class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();

    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    const facetForm = this.querySelector("form");
    facetForm.addEventListener("input", (evt) => {
      FacetFiltersForm.loading.start();
      this.debouncedOnSubmit(evt);
    });
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state
        ? event.state.searchParams
        : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener("popstate", onHistoryChange);
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    window.scroll({
      top: FacetFiltersForm.section.offsetTop - 100,
      left: 0,
      behavior: "smooth",
    });
    FacetFiltersForm.loading.finish();

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [
          ...FacetFiltersForm.filterData,
          { html, url },
        ];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderElements(
          html,
          "[data-product-container]",
          FacetFiltersForm.selectors.productGrid
        );
        FacetFiltersForm.renderElements(
          html,
          "[data-facet-form]",
          FacetFiltersForm.selectors.filtersForm
        );
        FacetFiltersForm.renderProductCount(html);

        document.dispatchEvent(new CustomEvent("collection:rerendered"));
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderElements(
      html,
      "[data-product-container]",
      FacetFiltersForm.selectors.productGrid
    );
    FacetFiltersForm.renderElements(
      html,
      "[data-facet-form]",
      FacetFiltersForm.selectors.filtersForm
    );
    FacetFiltersForm.renderProductCount(html);

    document.dispatchEvent(new CustomEvent("collection:rerendered"));
  }

  static renderElements(html, el, selector) {
    const elementContainer = document.querySelector(el);
    let caratState = null;
    if (elementContainer && el === "[data-facet-form]") {
      const caratRange = elementContainer.querySelector("carat-range");
      if (caratRange) {
        const minInput = caratRange.querySelector('[data-type="min-input"]');
        const maxInput = caratRange.querySelector('[data-type="max-input"]');
        const tab = caratRange.closest("collapsible-tab");
        caratState = {
          min: minInput ? minInput.value : "",
          max: maxInput ? maxInput.value : "",
          open: tab ? tab.getAttribute("open") === "true" : false,
        };
      }
    }
    elementContainer.innerHTML = new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(el).innerHTML;
    FacetFiltersForm.loading = new window.FoxTheme.AnimateLoading(
      document.body,
      { overlay: document.querySelector(selector) }
    );
    __reInitTooltip(elementContainer);
    if (caratState) {
      FacetFiltersForm.restoreCaratRange(elementContainer, caratState);
    }
  }

  static renderProductCount(html) {
    const count = new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(FacetFiltersForm.selectors.productCount).innerHTML;
    const container = document.querySelector(
      FacetFiltersForm.selectors.productCount
    );
    container.innerHTML = count;
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, "text/html");

    const facetDetailsElements = parsedHTML.querySelectorAll(
      "#FacetFiltersForm .js-filter"
    );
    const matchesIndex = (element) => {
      const jsFilter = event ? event.target.closest(".js-filter") : undefined;
      return jsFilter
        ? element.dataset.index === jsFilter.dataset.index
        : false;
    };
    const facetsToRender = Array.from(facetDetailsElements).filter(
      (element) => !matchesIndex(element)
    );

    facetsToRender.forEach((element) => {
      const target = document.querySelector(
        `.f-facets__block[data-index="${element.dataset.index}"]`
      );
      if (!target) return;
      const currentTab = target.querySelector("collapsible-tab");
      let keepOpenState = null;
      if (currentTab) {
        keepOpenState = currentTab.getAttribute("open") === "true";
      }
      target.textContent = "";
      const child =
        element.querySelector("collapsible-tab") ||
        element.querySelector("#Mobile-SortBy-Block");
      if (child && child.matches("collapsible-tab") && keepOpenState !== null) {
        if (keepOpenState) {
          child.setAttribute("open", "true");
        } else {
          child.removeAttribute("open");
        }
      }
      if (child) target.appendChild(child);
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = [".active-facets"];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML =
        activeFacetsElement.innerHTML;
    });
  }

  static updateURLHash(searchParams) {
    history.pushState(
      { searchParams },
      "",
      `${window.location.pathname}${searchParams && "?".concat(searchParams)}`
    );
  }

  static restoreCaratRange(container, fallbackState) {
    const caratRange = container.querySelector("carat-range");
    if (!caratRange) return;
    const paramMin = caratRange.dataset.paramMin;
    const paramMax = caratRange.dataset.paramMax;
    const paramsSource =
      FacetFiltersForm.searchParamsPrev || window.location.search.slice(1);
    const params = new URLSearchParams(paramsSource);
    const minFromUrl = paramMin ? params.get(paramMin) : null;
    const maxFromUrl = paramMax ? params.get(paramMax) : null;
    const valueInputs = [
      ...caratRange.querySelectorAll("[data-carat-value]"),
    ];
    const checkedValues = valueInputs
      .filter((input) => input.checked)
      .map((input) => caratRange.parseNumber(input.dataset.caratValue, 0));
    const hasCheckedValues = checkedValues.length > 0;
    const minValue = hasCheckedValues
      ? Math.min(...checkedValues)
      : minFromUrl !== null
        ? minFromUrl
        : fallbackState?.min ?? "";
    const maxValue = hasCheckedValues
      ? Math.max(...checkedValues)
      : maxFromUrl !== null
        ? maxFromUrl
        : fallbackState?.max ?? "";

    const minInput = caratRange.querySelector('[data-type="min-input"]');
    const maxInput = caratRange.querySelector('[data-type="max-input"]');
    if (minInput) minInput.value = minValue;
    if (maxInput) maxInput.value = maxValue;

    if (typeof caratRange.syncRangesFromInputs === "function") {
      caratRange.syncRangesFromInputs();
    }
    if (typeof caratRange.setMinAndMaxValues === "function") {
      caratRange.setMinAndMaxValues();
    }
    if (typeof caratRange.updateSliderTrack === "function") {
      caratRange.updateSliderTrack();
    }
    if (typeof caratRange.updateSelectedValues === "function") {
      caratRange.updateSelectedValues();
    }

    const tab = caratRange.closest("collapsible-tab");
    if (tab && (minValue !== "" || maxValue !== "" || fallbackState?.open)) {
      if (typeof tab.open === "function") {
        tab.open();
      } else {
        tab.setAttribute("open", "true");
      }
    }
  }

  static getSections() {
    return [
      {
        section: FacetFiltersForm.sectionId,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    if (form.id === "FacetFiltersForm" && !FoxThemeSettings.isMobile) {
      formData.delete("sort_by");
    }
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    const sortFilterForms = document.querySelectorAll(
      "facet-filters-form form"
    );
    if (FoxThemeSettings.isMobile) {
      const searchParams = this.createSearchParams(
        event.target.closest("form")
      );
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      sortFilterForms.forEach((form) => {
        forms.push(this.createSearchParams(form));
      });
      this.onSubmitForm(forms.join("&"), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    const url =
      event.currentTarget.href.indexOf("?") === -1
        ? ""
        : event.currentTarget.href.slice(
            event.currentTarget.href.indexOf("?") + 1
          );
    FacetFiltersForm.renderPage(url);
  }
}
FacetFiltersForm.selectors = {
  container: ".f-facets__container",
  productGrid: "[data-products-grid]",
  productCount: "[data-product-count]",
  filtersForm: "[data-facet-form]",
  facetWrapper: "[data-facets-wrapper]",
  facetBlock: ".f-facets__block",
};

FacetFiltersForm.section = document.querySelector("#ProductGridContainer");
FacetFiltersForm.sectionId =
  FacetFiltersForm.section && FacetFiltersForm.section.dataset.sectionId;
FacetFiltersForm.domNodes = queryDomNodes(
  FacetFiltersForm.selectors,
  FacetFiltersForm.section
);
FacetFiltersForm.loading = new window.FoxTheme.AnimateLoading(document.body, {
  overlay: document.querySelector(FacetFiltersForm.selectors.productGrid),
});

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define("facet-filters-form", FacetFiltersForm);
FacetFiltersForm.setListeners();
document.addEventListener("DOMContentLoaded", () => {
  const filtersForm = document.querySelector(
    FacetFiltersForm.selectors.filtersForm
  );
  if (filtersForm) {
    FacetFiltersForm.restoreCaratRange(filtersForm, null);
  }
});

// customElements.define('facet-filters-form', FacetFiltersForm)

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.selectors = {
      inputs: ['input[type="number"]'],
      ranges: ['input[type="range"]'],
      minRange: '[data-type="min-range"]',
      maxRange: '[data-type="max-range"]',
      minInput: '[data-type="min-input"]',
      maxInput: '[data-type="max-input"]',
    };
    this.domNodes = queryDomNodes(this.selectors, this);
    this.priceGap = parseInt(this.dataset.priceGap);
    this.priceMax = parseInt(this.dataset.priceMax);

    this.domNodes.inputs.forEach((el) =>
      el.addEventListener("change", this.onInputChange.bind(this))
    );
    this.domNodes.ranges.forEach((el) =>
      el.addEventListener("input", this.onSliderChange.bind(this))
    );

    this.setMinAndMaxValues();
  }

  onSliderChange(event) {
    const input = event.currentTarget;
    const type = input.dataset.type;
    const value = Number(input.value);
    const { minInput, maxInput, minRange, maxRange } = this.domNodes;
    if (type === "min-range") {
      if (maxRange.value - input.value >= this.priceGap) {
        minInput.value = input.value;
      } else {
        input.value = maxRange.value - this.priceGap;
      }
    }
    if (type === "max-range") {
      if (input.value - minRange.value >= this.priceGap) {
        maxInput.value = input.value;
      } else {
        input.value = Number(minRange.value) + Number(this.priceGap);
      }
    }
    this.style.setProperty(
      "--price-min",
      (100 * minRange.value) / this.priceMax + "%"
    );
    this.style.setProperty(
      "--price-max",
      (100 * maxRange.value) / this.priceMax + "%"
    );
  }

  onInputChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const { minInput, maxInput } = this.domNodes;
    if (maxInput.value)
      minInput.setAttribute("max", Number(maxInput.value) - this.priceGap);
    if (minInput.value)
      maxInput.setAttribute("min", Number(minInput.value) + this.priceGap);
    if (minInput.value === "") maxInput.setAttribute("min", 0);
    if (maxInput.value === "")
      minInput.setAttribute("max", maxInput.getAttribute("max"));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute("min"));
    const max = Number(input.getAttribute("max"));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define("price-range", PriceRange);

class CaratRange extends HTMLElement {
  constructor() {
    super();
    this.selectors = {
      inputs: ['input[type="number"]'],
      ranges: ['input[type="range"]'],
      minRange: '[data-type="min-range"]',
      maxRange: '[data-type="max-range"]',
      minInput: '[data-type="min-input"]',
      maxInput: '[data-type="max-input"]',
      valueInputs: ['[data-carat-value]'],
    };
    this.domNodes = queryDomNodes(this.selectors, this);

    this.rangeMin = this.parseNumber(this.dataset.rangeMin, 0);
    this.rangeMax = this.parseNumber(this.dataset.rangeMax, 0);
    this.rangeGap = this.parseNumber(this.dataset.rangeGap, 0);
    this.rangeStep = this.parseNumber(this.dataset.rangeStep, 1);
    this.decimals = this.getDecimals(this.rangeStep);
    this.isActive = this.dataset.rangeActive === "true";

    this.domNodes.inputs.forEach((el) =>
      el.addEventListener("change", this.onInputChange.bind(this))
    );
    this.domNodes.ranges.forEach((el) =>
      el.addEventListener("input", this.onSliderChange.bind(this))
    );

    this.setMinAndMaxValues();
    this.updateSliderTrack();
    this.updateSelectedValues();
  }

  parseNumber(value, fallback) {
    const parsed = parseFloat(value);
    return Number.isNaN(parsed) ? fallback : parsed;
  }

  getDecimals(step) {
    const stepString = step.toString();
    if (stepString.includes("e-")) {
      return parseInt(stepString.split("e-")[1], 10);
    }
    const decimals = stepString.split(".")[1];
    return decimals ? decimals.length : 0;
  }

  formatValue(value) {
    return Number(value).toFixed(this.decimals);
  }

  clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  getRangeSpan() {
    const span = this.rangeMax - this.rangeMin;
    return span > 0 ? span : this.rangeStep;
  }

  onSliderChange(event) {
    const input = event.currentTarget;
    const type = input.dataset.type;
    const value = this.parseNumber(input.value, this.rangeMin);
    const { minInput, maxInput, minRange, maxRange } = this.domNodes;
    if (type === "min-range") {
      if (this.parseNumber(maxRange.value, this.rangeMax) - value >= this.rangeGap) {
        minInput.value = this.formatValue(value);
      } else {
        const nextValue =
          this.parseNumber(maxRange.value, this.rangeMax) - this.rangeGap;
        input.value = this.formatValue(nextValue);
        minInput.value = this.formatValue(nextValue);
      }
    }
    if (type === "max-range") {
      if (value - this.parseNumber(minRange.value, this.rangeMin) >= this.rangeGap) {
        maxInput.value = this.formatValue(value);
      } else {
        const nextValue =
          this.parseNumber(minRange.value, this.rangeMin) + this.rangeGap;
        input.value = this.formatValue(nextValue);
        maxInput.value = this.formatValue(nextValue);
      }
    }
    this.updateSliderTrack();
    this.updateSelectedValues();
  }

  onInputChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.syncRangesFromInputs();
    this.updateSliderTrack();
    this.setMinAndMaxValues();
    this.updateSelectedValues();
  }

  syncRangesFromInputs() {
    const { minInput, maxInput, minRange, maxRange } = this.domNodes;
    if (minRange) {
      minRange.value =
        minInput.value !== "" ? minInput.value : this.formatValue(this.rangeMin);
    }
    if (maxRange) {
      maxRange.value =
        maxInput.value !== "" ? maxInput.value : this.formatValue(this.rangeMax);
    }
  }

  getCurrentRange() {
    const { minInput, maxInput } = this.domNodes;
    const minValue =
      minInput && minInput.value !== ""
        ? this.parseNumber(minInput.value, this.rangeMin)
        : this.rangeMin;
    const maxValue =
      maxInput && maxInput.value !== ""
        ? this.parseNumber(maxInput.value, this.rangeMax)
        : this.rangeMax;
    return { minValue, maxValue };
  }

  getAvailableValues() {
    const { valueInputs } = this.domNodes;
    if (!valueInputs || !valueInputs.length) return [];
    return valueInputs
      .map((input) => this.parseNumber(input.dataset.caratValue, this.rangeMin))
      .filter((value) => Number.isFinite(value))
      .sort((a, b) => a - b);
  }

  snapToAvailable(minValue, maxValue) {
    const values = this.getAvailableValues();
    if (!values.length) return { minValue, maxValue };
    const snapMin = values.find((value) => value >= minValue) ?? values[0];
    const snapMax =
      [...values].reverse().find((value) => value <= maxValue) ??
      values[values.length - 1];
    if (snapMin > snapMax) {
      return { minValue: snapMax, maxValue: snapMax };
    }
    return { minValue: snapMin, maxValue: snapMax };
  }

  isFullRange(minValue, maxValue) {
    const tolerance = this.rangeStep / 10;
    return (
      minValue <= this.rangeMin + tolerance &&
      maxValue >= this.rangeMax - tolerance
    );
  }

  updateSelectedValues() {
    const { valueInputs, minInput, maxInput } = this.domNodes;
    if (!valueInputs || !valueInputs.length) return;
    const currentRange = this.getCurrentRange();
    const { minValue, maxValue } = this.snapToAvailable(
      currentRange.minValue,
      currentRange.maxValue
    );
    if (minInput) minInput.value = this.formatValue(minValue);
    if (maxInput) maxInput.value = this.formatValue(maxValue);
    if (this.domNodes.minRange)
      this.domNodes.minRange.value = this.formatValue(minValue);
    if (this.domNodes.maxRange)
      this.domNodes.maxRange.value = this.formatValue(maxValue);

    if (this.isFullRange(minValue, maxValue) && !this.isActive) {
      valueInputs.forEach((input) => {
        input.checked = false;
      });
      if (minInput) minInput.value = "";
      if (maxInput) maxInput.value = "";
      return;
    }
    valueInputs.forEach((input) => {
      const v = this.parseNumber(input.dataset.caratValue, this.rangeMin);
      input.checked = v >= minValue && v <= maxValue;
    });
    this.isActive = true;
  }

  setMinAndMaxValues() {
    const { minInput, maxInput } = this.domNodes;
    if (maxInput.value !== "") {
      minInput.setAttribute(
        "max",
        this.formatValue(this.parseNumber(maxInput.value, this.rangeMax) - this.rangeGap)
      );
    } else {
      minInput.setAttribute("max", this.formatValue(this.rangeMax));
    }
    if (minInput.value !== "") {
      maxInput.setAttribute(
        "min",
        this.formatValue(this.parseNumber(minInput.value, this.rangeMin) + this.rangeGap)
      );
    } else {
      maxInput.setAttribute("min", this.formatValue(this.rangeMin));
    }
  }

  adjustToValidValues(input) {
    const value = this.parseNumber(input.value, this.rangeMin);
    const min = this.parseNumber(input.getAttribute("min"), this.rangeMin);
    const max = this.parseNumber(input.getAttribute("max"), this.rangeMax);
    input.value = this.formatValue(this.clamp(value, min, max));
  }

  updateSliderTrack() {
    const { minRange, maxRange } = this.domNodes;
    if (!minRange || !maxRange) return;
    const span = this.getRangeSpan();
    const minValue = this.parseNumber(minRange.value, this.rangeMin);
    const maxValue = this.parseNumber(maxRange.value, this.rangeMax);
    const minPercent = ((minValue - this.rangeMin) / span) * 100;
    const maxPercent = ((maxValue - this.rangeMin) / span) * 100;
    this.style.setProperty("--price-min", `${minPercent}%`);
    this.style.setProperty("--price-max", `${maxPercent}%`);
  }
}

customElements.define("carat-range", CaratRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    this.querySelector("a").addEventListener("click", (e) => {
      e.preventDefault();
      const form =
        this.closest("facet-filters-form") ||
        document.querySelector("facet-filters-form");
      form.onActiveFilterClick(e);
    });
  }
}

customElements.define("facet-remove", FacetRemove);

class ApplyButton extends HTMLElement {
  constructor() {
    super();
    const drawer = this.closest("drawer-component");
    this.querySelector("button").addEventListener("click", (e) => {
      e.preventDefault();
      drawer && drawer.closeDrawer(false);
    });
  }
}

customElements.define("facet-apply", ApplyButton);
